"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, FlaskConical, MessageSquare as MessageSquareCheck, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const highlights = [{
  id: 1,
  title: "Method Feasibility & Strategy",
  description: "Assessment of PAT, Raman, or NIR suitability for your specific formulation or process."
}, {
  id: 2,
  title: "Compliance & Qualification",
  description: "Full lifecycle validation documentation tailored to GMP and regulated laboratory standards."
}, {
  id: 3,
  title: "Dedicated Technical Support",
  description: "Direct consultation with application specialists across Ireland and the UK."
}];
export default function DiscussApplication() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="discussapplicationcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="page-container">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} variants={containerVariants}>
          <Card className="border border-border bg-card shadow-sm overflow-hidden rounded-2xl">
            <CardContent className="p-8 md:p-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                
                {/* Left Column: Heading and CTAs */}
                <div className="lg:col-span-7 flex flex-col gap-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary text-secondary-foreground text-xs font-medium w-fit border border-border">
                    <FlaskConical className="size-3.5 text-primary" />
                    <span>Application Engineering & Consultation</span>
                  </div>

                  <div className="space-y-4">
                    <h2 className="font-default text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground leading-tight">
                      Ready to evaluate your laboratory or inline process?
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg max-w-xl leading-relaxed">
                      Connect with our analytical instrumentation specialists to discuss feasibility, system specifications, and compliance requirements for your workflow.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-4 pt-2">
                    <Button asChild size="lg" className="rounded-md shadow-sm">
                      <Link to="/DiscussYourApplicationPage" className="inline-flex items-center gap-2">
                        <span>Discuss Your Application</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>

                    <Button asChild variant="outline" size="lg" className="rounded-md">
                      <Link to="/BookADemoPage">
                        Book a Demonstration
                      </Link>
                    </Button>
                  </div>

                  <div className="pt-4 border-t border-border/80 flex flex-wrap items-center gap-6 text-xs text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                      <ShieldCheck className="size-4 text-primary" />
                      <span>Confidential application review</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <MessageSquareCheck className="size-4 text-primary" />
                      <span>Response within 1 business day</span>
                    </div>
                  </div>
                </div>

                {/* Right Column: Highlights Card */}
                <div className="lg:col-span-5">
                  <div className="bg-secondary/60 rounded-xl p-6 md:p-8 border border-border flex flex-col gap-5">
                    <h3 className="font-default text-lg font-semibold text-secondary-foreground">
                      What to expect from our consultation
                    </h3>

                    <div className="space-y-4">
                      {highlights.map((item, i) => <div key={item.id} data-index={i} className="flex items-start gap-3.5">
                          <CheckCircle2 className="size-5 text-primary mt-0.5 shrink-0" />
                          <div className="space-y-1">
                            <h4 className="text-sm font-semibold text-foreground leading-snug">
                              {item.title}
                            </h4>
                            <p className="text-xs text-muted-foreground leading-relaxed">
                              {item.description}
                            </p>
                          </div>
                        </div>)}
                    </div>
                  </div>
                </div>

              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>;
}